/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';

import { Grid, Form, Header, Divider } from '@mairistem/composants';

import { OPTIONS_PUPILLE, OPTIONS_SITUATION_MATRIMONIALE } from '@/constants';
import type {
  Adresse,
  Individu,
  Pupille,
  Sexe,
  SituationFamiliale,
} from '@/types';

import type { TypeDateNaissance } from '@/enums';
import PanelButtonEdition from '../PanelButtonEdition';
import SituationFamilialeModal from './SituationFamilialeModal';
import InformationIndividu from '../InformationIndividu';

interface IdentificationPanelProps {
  identifiant: string | number;
  minuteNaissance: number;
  heureNaissance: number;
  jourNaissance: number;
  moisNaissance: number;
  anneeNaissance: number;
  typeDateNaissance: TypeDateNaissance;
  sexe: Sexe;
  nomFamille: string;
  nomUsage: string;
  prenom: string;
  prenomsComplementaires: string;
  adresseNaissance: Partial<Adresse>;
  situationFamiliale: SituationFamiliale;
  pupille: Pupille;
  nombreFreresSoeurs: number;
  nombreEnfants: number;
  dateRecensement?: string;
  recense: Partial<Individu>;
  updated: boolean;
  onChange: (data: Partial<Individu>) => void;
  onSave: () => Promise<void>;
  onRollback: () => void;
}

const IdentificationPanel = (props: IdentificationPanelProps) => {
  const {
    identifiant,
    minuteNaissance,
    heureNaissance,
    jourNaissance,
    moisNaissance,
    anneeNaissance,
    typeDateNaissance,
    sexe,
    nomFamille,
    nomUsage,
    prenom,
    prenomsComplementaires,
    adresseNaissance,
    dateRecensement,
    situationFamiliale,
    pupille,
    nombreFreresSoeurs,
    nombreEnfants,
    recense,
    updated,
    onChange,
    onSave,
    onRollback,
  } = props;

  const [modalEditionSituationOpen, setModalEditionSituationOpen] =
    React.useState<boolean>(false);

  return (
    <Grid as={Form}>
      <InformationIndividu
        identifiant={identifiant}
        minuteNaissance={minuteNaissance}
        heureNaissance={heureNaissance}
        jourNaissance={jourNaissance}
        moisNaissance={moisNaissance}
        anneeNaissance={anneeNaissance}
        typeDateNaissance={typeDateNaissance}
        sexe={sexe}
        nomFamille={nomFamille}
        nomUsage={nomUsage}
        prenom={prenom}
        prenomsComplementaires={prenomsComplementaires}
        adresseNaissance={adresseNaissance}
        dateRecensement={dateRecensement}
        recense={recense}
        updated={updated}
        title="INFORMATIONS DE NAISSANCE"
        titleModal="Informations d'état civil et de naissance du recensé"
        data-testid="panel-edit-modal"
        onChange={onChange}
        onSave={onSave}
        onRollback={onRollback}
      />
      <Divider />
      <Grid.Row>
        <Header as="h5" color="blue" style={{ margin: 0 }}>
          SITUATION FAMILIALE
        </Header>
        <PanelButtonEdition
          onClick={() => {
            setModalEditionSituationOpen(true);
          }}
          data-testid="panel-edit-modal-situation"
        />
        {modalEditionSituationOpen && (
          <SituationFamilialeModal
            situationFamiliale={situationFamiliale}
            pupille={pupille}
            nombreFreresSoeurs={nombreFreresSoeurs}
            nombreEnfants={nombreEnfants}
            updated={updated}
            onChange={onChange}
            onSave={onSave}
            onClose={() => {
              setModalEditionSituationOpen(false);
            }}
            onCancel={() => {
              onRollback();
              setModalEditionSituationOpen(false);
            }}
          />
        )}
      </Grid.Row>
      <Grid.Row>
        <Grid.Column
          mobile={16}
          tablet={12}
          computer={8}
          largeScreen={5}
          widescreen={4}
        >
          <span>
            <b>Situation matrimoniale : </b>
          </span>
          <span>
            {
              OPTIONS_SITUATION_MATRIMONIALE.find(
                (option) => option.value === recense?.situationFamiliale,
              )?.text
            }
          </span>
        </Grid.Column>
        <Grid.Column
          mobile={16}
          tablet={4}
          computer={8}
          largeScreen={3}
          widescreen={4}
        >
          <span>
            <b>Pupille : </b>
          </span>
          <span>
            {(recense?.pupille as string) === 'prefet'
              ? 'du préfet'
              : OPTIONS_PUPILLE.find(
                  (option) => option.value === recense?.pupille,
                )?.text || 'Aucun'}
          </span>
        </Grid.Column>
        <Grid.Column
          mobile={16}
          tablet={5}
          computer={8}
          largeScreen={3}
          widescreen={4}
        >
          <span>
            <b>Nombre de frères / soeurs vivants : </b>
          </span>
          <span>{recense?.nombreFreresSoeurs}</span>
        </Grid.Column>
        <Grid.Column
          mobile={16}
          tablet={11}
          computer={8}
          largeScreen={5}
          widescreen={4}
        >
          <span>
            <b>Nombre d&apos;enfants à charge : </b>
          </span>
          <span>{recense?.nombreEnfants}</span>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default IdentificationPanel;
